import React, {useEffect, useMemo, useState} from 'react';

import Layout, {Mode} from '../../components/Layout';
import MapView from './MapView';
import ProjectList from '@/components/ProjectList';
import Logout from '@/components/Login/Logout';

import LazyLoading from '@/components/LazyLoading';

import {useAsync} from 'react-use';
import {useTranslation} from 'react-i18next';

import {INITIAL_MAPSTYLE_URL} from '@/config';

import {getAllProjects} from '@/domain/useCases/getAllProjects';
import {getParcelsByProject} from '@/domain/useCases/getParcelsByProject';
import {getLoggedUser} from '@/domain/useCases/getLoggedUser';
import {getUsers} from '@/domain/useCases/getUsers';
import {getParcelSummary} from '@/domain/useCases/getParcelSummary';
import {getDisputeForParcel} from '@/domain/useCases/getDisputeForParcel';
import {createDispute} from '@/domain/useCases/createDispute';
import {updateDispute} from '@/domain/useCases/updateDispute';

import logoutUseCase from '@/domain/useCases/logoutUseCase';
import {useNavigate} from 'react-router-dom';
import {getStatsForProject} from '@/domain/useCases/getStatsForProject';
import WithoutParcelsDialog from '@/components/Panels/WithoutParcelsDialog';

import {Dispute} from '@/domain/entities/Dispute';
import {MultiPolygon, Polygon} from 'geojson';
import {DocumentProperties, ParcelDocumentCreation} from '@/domain/entities/ParcelDocument';
import {
  getParcelDocuments,
  createParcelDocument,
  deleteParcelDocument,
  getDocumentData
} from '@/domain/useCases/parcelDocumentUseCases';
import {getDisputesOverlayByProject} from '@/domain/useCases/getDisputesOverlayByProject';

const Index = () => {

  const {t, i18n} = useTranslation();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>();
  const [mapStyle, setMapStyle] = useState(INITIAL_MAPSTYLE_URL);
  const [selectedOverlays, setSelectedOverlays] = useState<Array<string>>(['parcelFill']);
  const [selectedProjectUuid, setSelectedProjectUuid] = useState<string>();
  const [selectedParcelUuid, setSelectedParcelUuid] = useState<string>();
  const [newGeom, setNewGeom] = useState<MultiPolygon>();
  const [newDispute, setNewDispute] = useState<Dispute>();
  const [parcelDocuments, setParcelDocuments] = useState<Array<DocumentProperties>>([]);
  const [isGeometryVisible, setGeometryVisibility] = useState<boolean>(false);

  //PROJECTS
  const {error: projectsError, loading: projectsLoading, value: projects} = useAsync(() => getAllProjects(), []);
  const selectedProject = projects?.filter(project => project.uuid === selectedProjectUuid)[0];

  //PARCELS
  const {error: parcelsError, loading: parcelsLoading, value: parcels} = useAsync(() => {
    if (!selectedProjectUuid) return Promise.resolve([]);
    return getParcelsByProject(selectedProjectUuid);
  }, [selectedProjectUuid]);

  //USERS
  const {error: userError, loading: userLoading, value: user} = useAsync(() => getLoggedUser(), []);
  const {error: usersError, loading: usersLoading, value: users} = useAsync(() => getUsers(), []);

  //DISPUTES
  const {error: disputesError, loading: disputesLoading, value: dispute} = useAsync(() => {
    if (!selectedParcelUuid) return Promise.resolve(undefined);
    return getDisputeForParcel(selectedParcelUuid);
  }, [selectedParcelUuid]);

  //STATS
  const {error: statsError, loading: statsLoading, value: stats} = useAsync(() => {
    if (!selectedProjectUuid) return Promise.resolve(undefined);
    return getStatsForProject(selectedProjectUuid);
  }, [selectedProjectUuid]);

  //DOCUMENTS
  const {error: documentsError, loading: documentsLoading, value: documents} = useAsync(() => {
    if (!selectedProjectUuid) return Promise.resolve(undefined);
    if (!selectedParcelUuid) return Promise.resolve(undefined);
    return getParcelDocuments(selectedParcelUuid);
  }, [selectedProjectUuid, selectedParcelUuid]);

  useEffect(() => {
    if(documents) {
      setParcelDocuments(documents);
    }
  }, [documents]);

  //DISPUTE LAYER
  const {error: disputesOverlayError, value: disputesOverlay} = useAsync(() => {
    if (!selectedProjectUuid) return Promise.resolve(undefined);
    if (mode !== Mode.SETTINGS) return Promise.resolve(undefined);
    return getDisputesOverlayByProject(selectedProjectUuid);
  }, [mode, selectedProjectUuid]);


  //ERROR AND LOADING
  const error = projectsError || parcelsError || userError || usersError || disputesError || statsError || documentsError || disputesOverlayError;
  const loading = projectsLoading || parcelsLoading || userLoading || usersLoading || disputesLoading || statsLoading || documentsLoading;

  const handleSelectedParcelUuid = (uuid: string | undefined) => {
    setSelectedParcelUuid(uuid);
    setNewGeom(undefined);
    setNewDispute(undefined);
    setParcelDocuments([]);
  };

  const selectedParcel = useMemo(() => parcels?.filter(p => p.uuid === selectedParcelUuid)[0], [parcels, selectedParcelUuid]);

  const parcelSummary = useMemo(() => {
    if(selectedParcelUuid && parcels) {
      return getParcelSummary({selectedParcelUuid, selectedProjectUuid, parcels, users, projects});
    }
    return undefined;
  }, [selectedParcelUuid, parcels, projects, users]);

  const handleDisputeGeomChanged = (geom: Polygon) => {
    if(geom){
      setNewGeom({
        type: 'MultiPolygon',
        coordinates: [geom.coordinates]
      });
    }
  };
  useEffect(() => {
    if(parcelSummary && !dispute) {
      setNewGeom(parcelSummary.geom);
    }
  }, []);

  const handleLogout = () => {
    logoutUseCase().then(() => navigate(`/${i18n.resolvedLanguage}/login`));
    setSelectedParcelUuid(undefined);
    setSelectedProjectUuid(undefined);
  };

  const handleProjectListClicked = () => {
    setSelectedParcelUuid(undefined);
    setSelectedProjectUuid(undefined);
  };

  const handleDisputeSaved = async (newDispute: Omit<Dispute, 'geom'>) => {
    const disputeWithGeom: Dispute = {...newDispute, geom: newGeom ? newGeom: selectedParcel!.geom};
    try {
      if (!newDispute.uuid) {
        const result = await createDispute(disputeWithGeom);
        setNewDispute(result);
        setMode(Mode.PARCEL);
      } else {
        const result = await updateDispute(disputeWithGeom);
        setNewDispute(result);
        setMode(Mode.PARCEL);
      }
    } catch (err) {
      console.error('Error updating dispute:', err);
    }
  };

  const handleDocumentUpload = async (dataForUpload: ParcelDocumentCreation) => {
    try {
      await createParcelDocument(dataForUpload);
      const newDocuments = await getParcelDocuments(dataForUpload.parcelUuid);
      setParcelDocuments(newDocuments);
    } catch (err) {
      console.error('Error updating parcel document creation:', err);
    }
  };

  const handleDocumentDelete = async (documentUuid: string) => {
    try {
      await deleteParcelDocument(documentUuid);
      const newParcelDocuments = parcelDocuments.filter(document => document.uuid !== documentUuid);
      setParcelDocuments(newParcelDocuments);
    } catch (err) {
      console.error('Error updating dispute:', err);
    }
  };

  const handleDocumentDownload = async (dataUrl: string, fileName: string) => {
    try {
      const blob = await getDocumentData(dataUrl);
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando documento:', err);
    }
  };

  const handleOverLayerChange = (newOverLayerIds: Array<string>) => newOverLayerIds !== selectedOverlays && setSelectedOverlays(newOverLayerIds);

  const handleProjectDeselect = () => setSelectedProjectUuid(undefined);

  const hasParcels = !!parcels?.length;

  return <>
    {loading &&
      <LazyLoading/>
    }
    {!loading && selectedProjectUuid && !hasParcels &&
      <WithoutParcelsDialog onProjectDeselect={handleProjectDeselect}/>
    }
    {!hasParcels ?
      <>
        <Logout onLogout={handleLogout}/>
        <ProjectList
          projects={projects || []}
          onSelectedProject={setSelectedProjectUuid}
          errorMessage={error && t('errors.loadingData')}
          onLogout={handleLogout}
        />
      </>
      :
      <Layout
        user={user}
        stats={stats}
        mapStyle={mapStyle}
        selectedOverlays={selectedOverlays}
        onMapStyleChanged={setMapStyle}
        onOverlaysChanged={handleOverLayerChange}
        selectedParcelUuid={selectedParcelUuid}
        parcelSummary={parcelSummary}
        mode={mode}
        dispute={newDispute || dispute}
        documents={parcelDocuments}
        project={selectedProject}
        onModeChanged={setMode}
        onProjectListClicked={handleProjectListClicked}
        onLogout={handleLogout}
        onDisputeSaved={handleDisputeSaved}
        toggleGeometryVisibility={setGeometryVisibility}
        onDocumentUpload={handleDocumentUpload}
        onDocumentDownload={handleDocumentDownload}
        onDocumentDelete={handleDocumentDelete}
      >
        <MapView
          project={selectedProject}
          selectedOverlays={selectedOverlays}
          selectedParcel={selectedParcel}
          user={user}
          parcels={parcels}
          mapStyle={mapStyle}
          disputesOverlay={disputesOverlay}
          isGeometryVisible={isGeometryVisible}
          disputeGeom={newDispute?.geom || dispute?.geom}
          onSelectedParcelUuid={handleSelectedParcelUuid}
          onDisputeGeomChanged={handleDisputeGeomChanged}
          onModeChanged={setMode}
        />
      </Layout>
    }
  </>;
};

export default Index;
